/**
 * Type definitions for the layout architecture.
 */

import {MaskOptions} from '@wandb/weave/common/types/media';

import {ClassLabelMap} from '../../../util/media';
import {RunWithHistoryAndMediaWandb, TileMediaSpec} from '../../MediaCard';
import {MediaBrowserPanelConfig} from '../common';
import {StepMeta} from '../types';

// ============================================================================
// Data Transformation (WHAT to show)
// ============================================================================

/**
 * GridData: Explicit structure representing layout after transformation.
 *
 * This is the output of transform functions (transformForGallery, etc.)
 * and the input to rendering components.
 */
export type GridData = {
  rows: GridRow[];
  metadata: GridMetadata;
};

/**
 * GridRow: A single row in the layout.
 */
export type GridRow = {
  cells: GridCell[];
  rowLabel?: string;
  rowKey: string;
};

/**
 * GridCell: A single cell in the layout containing one tile.
 */
export type CompleteGridCell = {
  /** Run this tile belongs to */
  run: RunWithHistoryAndMediaWandb;

  step: number;
  mediaIndex: number;
  mediaKey: string;
  tileSpec: TileMediaSpec;
  labels: Record<string, string | number>;
  maskOptions?: MaskOptions;
  isEmpty?: boolean;
};

export type EmptyGridCell = {
  isEmpty: true;
  mediaKey: string;
};

export type GridCell = CompleteGridCell | EmptyGridCell;

/**
 * GridMetadata: Additional information about the grid.
 */
export type GridMetadata = {
  layoutType: LayoutMode;
  totalCells: number;
  axisData?: {
    x?: number[];
    y?: number[];
  };
  historySampleRate?: number;
  xAxisTicks?: number[];
  allXAxisValues?: number[];
  yAxisTicks?: number[];
  allYAxisValues?: number[];
};

/**
 * SlotRegistry: Defines WHAT controls appear at each location in the layout.
 *
 * Keys are physical locations in the UI (matching the visual structure).
 * Values are arrays of controls to render at that location.
 *
 * Visual Structure:
 * ┌─────────────────────────────────────┐
 * │      CONTENT HEADER (full width)    │
 * ├───────────┬───────────┬─────────────┤
 * │ COL HEAD  │ COL HEAD  │ COL HEAD    │
 * ├───────────┴───────────┴─────────────┤
 * │      ROW HEADER (full width)        │
 * ├───────────┬───────────┬─────────────┤
 * │ TITLE     │ TITLE     │ TITLE       │
 * │ MEDIA     │ MEDIA     │ MEDIA       │
 * │ CAPTION   │ CAPTION   │ CAPTION     │
 * ├───────────┴───────────┴─────────────┤
 * │      ROW FOOTER (full width)        │
 * ├───────────┬───────────┬─────────────┤
 * │ COL FOOT  │ COL FOOT  │ COL FOOT    │
 * ├───────────┴───────────┴─────────────┤
 * │      CONTENT FOOTER (full width)    │
 * └─────────────────────────────────────┘
 */
export type SlotRegistry = {
  'content-header'?: ControlDefinition[];
  'column-header'?: ControlDefinition[];
  'row-header'?: ControlDefinition[];
  'tile-header'?: ControlDefinition[];
  'tile-footer'?: ControlDefinition[];
  'row-footer'?: ControlDefinition[];
  'column-footer'?: ControlDefinition[];
  'content-footer'?: ControlDefinition[];
};

/**
 * Tile dimensions that can be displayed in titles/headers.
 */
export type TileDimension = 'run' | 'step' | 'index';

/**
 * Axis controls for grid/gallery layouts.
 */
export type AxisControl = 'xAxisRange' | 'yAxisRange';

/**
 * All possible control types.
 */
export type ControlType = TileDimension | AxisControl | 'caption';

/**
 * Control rendering types.
 *
 * - Interactive types (slider/tabs/dropdown/range-slider): Rendered as interactive controls
 * - 'label': Not rendered as interactive control, but shown as static text in headers/footers
 */
export type ControlRenderType =
  | 'slider'
  | 'tabs'
  | 'dropdown'
  | 'range-slider'
  | 'label';

/**
 * ControlDefinition: Defines a single control to be rendered.
 */
export type ControlDefinition = {
  /** Which control this is (maps to state keys) */
  control: ControlType;

  /**
   * How to render the control:
   * - 'slider', 'tabs', 'dropdown', 'range-slider': Interactive controls
   * - 'label': Static label shown in headers/footers (not an interactive control)
   */
  type: ControlRenderType;

  /** Whether control is shared (linked) across all tiles or per-tile */
  linked: boolean;
};

/**
 * Type guard to check if a control type is a tile dimension.
 *
 * @param control - The control type to check
 * @returns True if the control is a tile dimension (run/step/index)
 *
 * @example
 * ```typescript
 * if (isTileDimension(control.control)) {
 *   // control.control is now typed as TileDimension
 *   displayTitle(control.control);
 * }
 * ```
 */
export function isTileDimension(
  control: ControlType
): control is TileDimension {
  return control === 'run' || control === 'step' || control === 'index';
}

/**
 * TransformInput: All data needed by a transform function.
 */
export type TransformInput = {
  runs: RunWithHistoryAndMediaWandb[];
  stepMeta: StepMeta;
  currentStep: number;
  currentIndex: number;
  config: MediaBrowserPanelConfig;
  allMasks: ClassLabelMap;
  allBoxes: ClassLabelMap;
};

/**
 * TransformFunction: Function that transforms run data into layout structure.
 *
 * Transform functions should be pure:
 * - Same inputs → same outputs
 * - No side effects
 * - No API calls
 * - Easy to test
 */
export type TransformFunction = (input: TransformInput) => GridData;

// ============================================================================
// Helper Types
// ============================================================================

/**
 * Types of data to display on the axis.
 */
export type AxisType = 'run' | 'step' | 'index' | 'camera';

/**
 *  Enum for ways to display media.
 */
export type LayoutMode = 'gallery' | 'grid';
